import { t } from '@lingui/core/macro';
import type { ViewMode, Folder, Tag, User } from '../types/api';
import type { IconProps } from '../components/ui/Icon';

export interface PageInfo {
  icon: IconProps['name'];
  label: string;
  count?: number | null;
}

/** Extra context that isn't derivable from the user/folder/tag models — currently
 *  only the active search term and result count for the "search" section. */
export interface PageInfoOptions {
  searchTerm?: string;
  searchCount?: number | null;
}

export function getPageInfo(
  mode: ViewMode,
  currentUser?: User,
  currentFolder?: Folder,
  currentTag?: Tag,
  folderTitle?: string,
  tagSlug?: string,
  options?: PageInfoOptions,
): PageInfo | null {
  switch (mode) {
    case 'home':
    case 'unread':
      return {
        icon: 'home',
        label: t`Home`,
        count: currentUser ? (currentUser.counts?.unread ?? 0) : undefined,
      };
    case 'liked':
      return {
        icon: 'like',
        label: t`Liked`,
        count: currentUser ? (currentUser.counts?.liked ?? 0) : undefined,
      };
    case 'archive':
      return {
        icon: 'archive',
        label: t`Archive`,
        count: currentUser ? (currentUser.counts?.archived ?? 0) : undefined,
      };
    case 'videos':
      return { icon: 'video', label: t`Videos`, count: null };
    case 'highlights':
      return {
        icon: 'note',
        label: t`Notes`,
        count: currentUser ? (currentUser.counts?.notes ?? 0) : undefined,
      };
    case 'folder':
      if (currentFolder) {
        return { icon: 'folder', label: currentFolder.title, count: currentFolder.count };
      } else if (folderTitle) {
        return { icon: 'folder', label: folderTitle, count: null };
      }
      return null;
    case 'tags':
      if (currentTag) {
        return { icon: 'tag', label: currentTag.name, count: currentTag.count };
      } else if (tagSlug) {
        const tagName = tagSlug.split('-').join(' ');
        return { icon: 'tag', label: tagName, count: null };
      }
      return null;
    case 'search':
      return {
        icon: 'search',
        label: options?.searchTerm || t`Search`,
        count: options?.searchCount ?? null,
      };
    default:
      return null;
  }
}
